import { motion } from "framer-motion";
import {
  RefreshCw, TrendingUp, GitBranch, Search, AlertOctagon, Recycle,
  FileText, Activity, FileSignature, Truck, Warehouse, Route, Settings2, ArrowRight,
} from "lucide-react";
import { SERVICE_IMAGES } from "@/lib/media";

export const SERVICES = [
  { id: "change-management", icon: RefreshCw, t: "Change Management", d: "Successfully implement change with minimum disruption and maximum employee adoption." },
  { id: "continuous-improvement", icon: TrendingUp, t: "Continuous Improvement", d: "Identify opportunities to improve efficiency, quality, productivity, and customer satisfaction." },
  { id: "process-mapping", icon: GitBranch, t: "Process Mapping", d: "Document current-state and future-state processes using professional workflow methodologies." },
  { id: "gap-analysis", icon: Search, t: "Gap Analysis", d: "Identify performance gaps, compliance gaps, process inefficiencies, and operational risks." },
  { id: "bottleneck-identification", icon: AlertOctagon, t: "Bottleneck Identification", d: "Analyze workflows to spot delays, unnecessary approvals, and resource constraints." },
  { id: "waste-reduction", icon: Recycle, t: "Waste Reduction", d: "Eliminate non-value-added activities that increase costs and reduce profitability." },
  { id: "sop-development", icon: FileText, t: "SOP Development", d: "Create scalable Standard Operating Procedures driving consistency and accountability." },
  { id: "kpi-development", icon: Activity, t: "KPI Development", d: "Design meaningful performance metrics that provide visibility into business operations." },
  { id: "contract-evaluation", icon: FileSignature, t: "Contract Evaluation & Review", d: "Review contracts to surface hidden risks, liabilities, and negotiation opportunities." },
  { id: "supply-chain", icon: Truck, t: "Supply Chain Optimization", d: "End-to-end visibility, supplier performance, lead time reduction, and cost control." },
  { id: "warehouse", icon: Warehouse, t: "Warehouse Improvement", d: "Layout, slotting, inventory accuracy, and labor productivity optimization." },
  { id: "logistics", icon: Route, t: "Logistics Optimization", d: "Routing, fleet utilization, on-time delivery, and last-mile excellence." },
  { id: "bpr", icon: Settings2, t: "Business Process Reengineering", d: "Rebuild critical processes from the ground up to unlock breakthrough performance." },
];

export default function Solutions({ onContact }) {
  return (
    <section id="services" className="bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="section-eyebrow mb-5">Our Solutions</div>
          <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#0A2540] leading-tight">
            How Clinivon creates business excellence
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Thirteen integrated capabilities that turn red problem areas into green optimized workflows —
            with measurable outcomes you can defend in the boardroom.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: (i % 3) * 0.06 }}
              className="bg-white group cursor-default flex flex-col"
            >
              <div className="relative aspect-[16/9] overflow-hidden bg-slate-100">
                <img
                  src={SERVICE_IMAGES[s.id]}
                  alt={s.t}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A2540]/70 via-[#0A2540]/10 to-transparent" />
                <div className="absolute top-3 left-3 h-9 w-9 grid place-items-center bg-white/95 text-[#0A2540] backdrop-blur-sm">
                  <s.icon size={18} />
                </div>
                <span className="absolute top-3 right-3 text-[10px] tabular text-white/80 font-bold">{String(i + 1).padStart(2, "0")}</span>
              </div>
              <div className="p-6 flex-1">
                <h3 className="font-display font-semibold text-lg text-[#0A2540] group-hover:text-[#10B981] transition-colors mb-2">{s.t}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{s.d}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-12 flex justify-center">
          <button
            onClick={onContact}
            className="btn-sharp inline-flex items-center gap-2 px-7 py-3.5 bg-[#0A2540] text-white text-sm font-semibold hover:bg-[#061A30] hover:-translate-y-0.5 transition-all"
          >
            Discuss your business challenge <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </section>
  );
}
